import { ArrowUpDown } from "lucide-react";
import { SquareArrowUp } from "lucide-react";
import {
  Balance,
  Mint,
  Role,
  useCreateSwapRequestMutation,
  useGetBalanceQuery,
  useGetPriceQuoteQuery,
  useGetWhitelistQuery,
  Whitelist,
} from "../generated/graphql";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { MintSelect } from "./ui/MintSelect";
import { useEffect, useState } from "react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useToast } from "./ui/use-toast";
import { Mfa } from "./Mfa";
import { useAuth } from "../AuthProvider";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface SwapCardProps {
  mintOptions?: Mint[];
  role?: Role;
}

export function SwapCard({ mintOptions, role }: SwapCardProps) {
  const { organizationId } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [selectedFromMint, setSelectedFromMint] = useState<Mint | null>(null);
  const [selectedToMint, setSelectedToMint] = useState<Mint | null>(null);
  const [fromAmount, setFromAmount] = useState<number>(1);

  const [searchParams] = useSearchParams();
  const fromMintId = searchParams.get("fromMintId");

  const [
    { data: balanceData, error: balanceError, fetching: balanceFetching },
    refetchBalance,
  ] = useGetBalanceQuery({
    variables: { organizationId: organizationId || "" },
    requestPolicy: "network-only",
    pause: organizationId === "" || !organizationId,
  });
  const [{ error: swapError, data: swapData }, createSwap] =
    useCreateSwapRequestMutation();

  const [{ error: whitelistError, data: whitelistData }] = useGetWhitelistQuery(
    {
      variables: {
        organizationId: organizationId || "",
      },
      pause: organizationId === "" || !organizationId,
    }
  );

  useEffect(() => {
    if (swapError) {
      toast({
        title: "Error",
        description: swapError.message,
        variant: "destructive",
      });
    }

    if (balanceError) {
      toast({
        title: "Error",
        description: balanceError.message,
        variant: "destructive",
      });
    }

    if (whitelistError) {
      toast({
        title: "Error",
        description: whitelistError.message,
        variant: "destructive",
      });
    }
  }, [balanceError, swapError, toast, whitelistError]);

  useEffect(() => {
    if (swapData) {
      toast({
        title: "Success",
        description: "Your swap is now being processed.",
        variant: "default",
      });
      navigate(`/swaps/${swapData.createSwapRequest.id}`);
    }
  }, [swapData, toast, navigate]);

  useEffect(() => {
    if (balanceFetching) {
      return;
    }
    const timerId = setTimeout(() => {
      refetchBalance({ requestPolicy: "network-only" });
    }, 30 * 1000);

    return () => clearTimeout(timerId);
  }, [balanceFetching, refetchBalance]);

  useEffect(() => {
    if (fromMintId && mintOptions) {
      const selectedMint = mintOptions.find(
        (m) => m.id.toString() === fromMintId
      );
      if (selectedMint) {
        setSelectedFromMint(selectedMint);
      }
    }
  }, [fromMintId, mintOptions]);

  const balances = balanceData?.getBalance;
  const whitelist = whitelistData?.getWhitelist;

  const availableMintOptions =
    mintOptions?.filter((mint) =>
      balances?.some(
        (balance) =>
          balance?.mintId === mint?.id && parseFloat(balance.credit) > 0
      )
    ) || [];

  useEffect(() => {
    if (selectedFromMint && balances) {
      const selectedBalance = balances.find(
        (b) => b?.mintId === selectedFromMint.id
      );
      if (selectedBalance) {
        setFromAmount(parseFloat(selectedBalance.credit));
      }
    }
  }, [selectedFromMint, balances]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-semibold flex items-center">
          Swap & Withdraw
          <SquareArrowUp className="ml-2 h-4 w-4 opacity-50" />
        </CardTitle>
      </CardHeader>
      {role === Role.Swap ? (
        <CardContent
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div className="mb-4 mt-4 flex flex-col gap-2">
            <Label>From</Label>
            <MintSelect
              mintOptions={availableMintOptions}
              onSelect={(m) => setSelectedFromMint(m)}
              selectedMint={selectedFromMint}
            />

            {selectedFromMint && (
              <>
                <Label>Amount</Label>
                <Input
                  type="number"
                  value={fromAmount}
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    const maxValue =
                      selectedFromMint && balances
                        ? parseFloat(
                            balances.find(
                              (b) => b?.mintId === selectedFromMint.id
                            )?.credit || "0"
                          )
                        : 0;
                    setFromAmount(Math.min(newValue, maxValue));
                  }}
                  min={0}
                  step={0.000001}
                  disabled={!selectedFromMint}
                  className="w-full"
                />
              </>
            )}
            {selectedFromMint && balances && (
              <span
                className="text-xs text-gray-500"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {balances.find((b) => b?.mintId === selectedFromMint.id)
                  ?.credit || "0"}{" "}
                {selectedFromMint.symbol} available
              </span>
            )}
          </div>
          {selectedFromMint && balances && (
            <SelectSwap
              mintOptions={mintOptions?.filter(
                (m) => m.id !== selectedFromMint.id
              )}
              balance={
                balances.find((b) => b?.mintId === selectedFromMint.id) ||
                balances[0]
              }
              role={role}
              fromAmount={fromAmount}
              organizationId={organizationId || ""}
              whiteListedAddresses={whitelist}
              selectedFromMint={selectedFromMint}
              selectedToMint={selectedToMint}
              setSelectedToMint={setSelectedToMint}
              executeSwap={async (quoteId, address, secret) => {
                const decimals = selectedFromMint?.decimals ?? 0;
                const fromAmountStr = fromAmount.toFixed(decimals);
                const fromAmountWithDecimals = BigInt(
                  fromAmountStr.replace(".", "")
                );

                await createSwap({
                  swapRequest: {
                    organizationId: organizationId || "",
                    fromMintId: selectedFromMint?.id,
                    fromAmount: fromAmountWithDecimals.toString(),
                    toMintId: selectedToMint?.id ?? 0,
                    endAddress: address,
                    secret,
                  },
                });
                refetchBalance();
              }}
            />
          )}
        </CardContent>
      ) : (
        <CardContent>
          <div>
            <p style={{ textAlign: "center" }}>
              You are not authorized to swap tokens in this organization.
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

interface SelectSwapProps {
  mintOptions?: Mint[];
  whiteListedAddresses?: Whitelist[];
  balance: Balance;
  role?: Role;
  organizationId: string;
  executeSwap: (quoteId: string, address: number, secret: string) => void;
  selectedFromMint: Mint | null;
  selectedToMint: Mint | null;
  setSelectedToMint: (mint: Mint | null) => void;
  fromAmount: number;
}

const SelectSwap = ({
  mintOptions,
  whiteListedAddresses,
  balance,
  organizationId,
  executeSwap,
  selectedFromMint,
  selectedToMint,
  setSelectedToMint,
  fromAmount,
}: SelectSwapProps) => {
  const { toast } = useToast();
  const [address, setAddress] = useState<number | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState("");

  const decimals = selectedFromMint?.decimals ?? 0;
  const fromAmountStr = fromAmount.toFixed(decimals);
  const fromAmountWithDecimals = BigInt(fromAmountStr.replace(".", ""));

  const [
    {
      data: priceQuoteData,
      error: priceQuoteError,
      fetching: priceQuoteFetching,
    },
    refetchPriceQuote,
  ] = useGetPriceQuoteQuery({
    variables: {
      fromMintId: balance?.mintId,
      toMintId: selectedToMint?.id ?? 0,
      organizationId,
      fromAmount: fromAmountWithDecimals.toString(),
    },
    pause:
      selectedToMint === null ||
      selectedFromMint === null ||
      Number(fromAmountWithDecimals) === 0,
  });

  useEffect(() => {
    if (priceQuoteError) {
      toast({
        title: "Error",
        description: priceQuoteError.message,
        variant: "destructive",
      });
    }
  }, [priceQuoteError, toast]);

  useEffect(() => {
    if (selectedToMint === null) {
      return;
    }

    // interval to refetch price quote
    const timerId = setInterval(() => {
      refetchPriceQuote({ requestPolicy: "network-only" });
    }, 30 * 1000); // 30 seconds

    return () => clearInterval(timerId);
  }, [selectedToMint, balance?.mintId, organizationId, refetchPriceQuote]);

  const priceQuote = priceQuoteData?.getPriceQuote;

  useEffect(() => {
    if (!priceQuote) return;

    const updateTimeRemaining = () => {
      const now = new Date();
      const expiresAt = new Date(priceQuote.expiresAt);
      const diffInSeconds = Math.max(
        0,
        Math.floor((expiresAt.getTime() - now.getTime()) / 1000)
      );

      if (diffInSeconds <= 0) {
        setTimeRemaining("Expired");
        refetchPriceQuote({ requestPolicy: "network-only" });
      } else if (diffInSeconds < 60) {
        setTimeRemaining(
          `Expires in ${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""}`
        );
      } else {
        const minutes = Math.floor(diffInSeconds / 60);
        const seconds = diffInSeconds % 60;
        setTimeRemaining(
          `${minutes} minute${minutes !== 1 ? "s" : ""} ${seconds} second${
            seconds !== 1 ? "s" : ""
          }`
        );
      }
    };

    updateTimeRemaining();
    const intervalId = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(intervalId);
  }, [priceQuote, refetchPriceQuote]); // Add refetchPriceQuote to the dependency array

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <div style={{ display: "flex", justifyContent: "center" }}>
        <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
      </div>

      <div className="mb-4 flex flex-col gap-2">
        <Label>To</Label>
        <MintSelect
          mintOptions={mintOptions}
          onSelect={(m) => {
            setSelectedToMint(m);
            setAddress(null);
          }}
          selectedMint={selectedToMint}
        />
      </div>

      {priceQuoteFetching ? (
        <div className="mb-4 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">Fetching quote...</span>
        </div>
      ) : (
        priceQuote && (
          <div className="mb-4">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                }}
              >
                Quote
              </div>
              <div
                style={{
                  fontSize: "10px",
                }}
              >
                {timeRemaining}
              </div>
            </div>

            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "5px",
                backgroundColor: "#fbfbfb",
                padding: "10px",
                width: "350px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "center" }}>
                {fromAmount} {selectedFromMint?.symbol} = {priceQuote.toAmount}{" "}
                {selectedToMint?.symbol}
              </div>
            </div>
          </div>
        )
      )}

      {selectedToMint && (
        <div
          className="mb-4 mt-4"
          style={{ maxWidth: "350px", width: "350px" }}
        >
          <Label style={{ marginLeft: "2px" }}>To Withdrawal Address</Label>
          {whiteListedAddresses && whiteListedAddresses.length > 0 ? (
            <Select
              value={address?.toString() ?? ""}
              onValueChange={(v) => {
                setAddress(parseInt(v));
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select address" />
              </SelectTrigger>
              <SelectContent>
                {whiteListedAddresses
                  ?.filter((wl) => wl.chain === selectedToMint.chain)
                  .map((wl, idx) => (
                    <SelectItem key={idx} value={wl.id.toString()}>
                      {wl.address}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm text-yellow-600 bg-yellow-100 border border-yellow-300 rounded-md p-2 mt-2">
              ⚠️ No whitelisted addresses available.
              <br />
              Please contact admin.
            </div>
          )}
        </div>
      )}

      {selectedToMint && address != null && priceQuote != null && (
        <Button
          type="submit"
          style={{
            width: "350px",
            marginBottom: "20px",
          }}
          onClick={() => {
            setQuoteId(priceQuote.id);
          }}
        >
          Swap & Withdraw
          <SquareArrowUp
            className="ml-2 h-4 w-4"
            style={{
              color: "white",
            }}
          />
        </Button>
      )}

      <Mfa
        isOpen={quoteId != null}
        onOtpComplete={(secret) => {
          if (quoteId == null || address == null) {
            return;
          }
          executeSwap(quoteId, address, secret);
          setQuoteId(null);
          setAddress(null);
          setSelectedToMint(null);
        }}
        onClose={() => {
          setQuoteId(null);
        }}
      />
    </div>
  );
};
